import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import CwDataTable from "../../components/construction/CwDataTable"
import CwPageHeader from "../../components/construction/CwPageHeader"
import SpreadsheetImportZone from "../../components/construction/SpreadsheetImportZone"
import CompanyChangeAction from "../../components/construction/CompanyChangeAction"
import CwAddButton from "../../components/construction/CwAddButton"
import CwRowActions from "../../components/construction/CwRowActions"
import CwConfirmDialog from "../../components/construction/CwConfirmDialog"
import EmployeeFormModal from "../../components/construction/EmployeeFormModal"
import { useSelectedCompany } from "../../context/companyProvider"
import { t } from "../../i18n"
import { formatCompanyContext } from "../../utils/companyAccess"
import { displayEmployeeName } from "../../utils/sageCodes"
import { SageApiError } from "../../utils/sageApiError"
import { sageEmployeeService } from "../../services/sageEmployeeService"
import { SageEmployee } from "../../Interfaces"
import { CircularProgress } from "@mui/material"

export default function EmployeesPage() {
  const { selectedCompany, isAdmin, canManageSageMaster } = useSelectedCompany()
  const companyId = selectedCompany?.id
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<SageEmployee | null>(null)
  const [deleting, setDeleting] = useState<SageEmployee | null>(null)
  const [deleteError, setDeleteError] = useState("")
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  const { data, isLoading, error } = useQuery(
    ["sage-employees", companyId],
    () => sageEmployeeService.getAll({ managementCompanyId: companyId }),
    {
      enabled: !!companyId,
      suspense: false,
      useErrorBoundary: false,
    },
  )

  const rows = data?.data || []

  const refetch = () => {
    queryClient.invalidateQueries(["sage-employees", companyId])
  }

  const handleDelete = async () => {
    if (!companyId || !deleting) return
    setDeleteSubmitting(true)
    setDeleteError("")
    try {
      await sageEmployeeService.delete(companyId, deleting.id)
      setDeleting(null)
      refetch()
    } catch (deleteErr) {
      setDeleteError(deleteErr instanceof SageApiError && deleteErr.message
        ? deleteErr.message
        : t("employees.loadFailed"))
    } finally {
      setDeleteSubmitting(false)
    }
  }

  return (
    <div className="w-full">
      <CwPageHeader
        title={t("employees.title")}
        subtitle={selectedCompany ? formatCompanyContext(selectedCompany) : undefined}
        action={(
          <div className="flex flex-wrap items-center gap-3">
            {canManageSageMaster && companyId ? (
              <CwAddButton
                label={t("employees.add")}
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              />
            ) : null}
            <CompanyChangeAction />
          </div>
        )}
      />
      {isAdmin ? (
        <SpreadsheetImportZone
          kind="employees"
          managementCompanyId={companyId}
          onImported={refetch}
        />
      ) : null}
      {isLoading ? (
        <div className="bg-white rounded-2xl shadow-lg h-48 flex items-center justify-center">
          <CircularProgress />
        </div>
      ) : error ? (
        <p className="bg-white rounded-2xl shadow-lg px-5 py-8 text-center text-red-600 font-source">
          {t("employees.loadFailed")}
        </p>
      ) : (
        <CwDataTable
          columns={[
            { key: "employeeNum", label: t("employees.columnNumber") },
            { key: "name", label: t("employees.columnName"), render: (row) => displayEmployeeName(row) },
            { key: "jobDescription", label: t("employees.columnRole") },
            ...(canManageSageMaster ? [{
              key: "actions",
              label: t("common.actions"),
              render: (row: SageEmployee) => (
                <CwRowActions
                  onEdit={() => {
                    setEditing(row)
                    setFormOpen(true)
                  }}
                  onDelete={() => {
                    setDeleteError("")
                    setDeleting(row)
                  }}
                />
              ),
            }] : []),
          ]}
          rows={rows}
          empty={t("employees.empty")}
        />
      )}
      {companyId ? (
        <EmployeeFormModal
          isOpen={formOpen}
          companyId={companyId}
          employee={editing}
          onClose={() => {
            setFormOpen(false)
            setEditing(null)
          }}
          onSaved={refetch}
        />
      ) : null}
      <CwConfirmDialog
        isOpen={Boolean(deleting)}
        title={t("employees.deleteTitle")}
        message={t("employees.deleteConfirm")}
        error={deleteError}
        submitting={deleteSubmitting}
        onConfirm={() => void handleDelete()}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleting(null)
            setDeleteError("")
          }
        }}
      />
    </div>
  )
}
